import React, { useEffect } from "react";
import RapiDocReact from "../components/RapidDocReact";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import TitleDescription from "../components/TitleDescription";
import Title from "../components/Title";
import TitleDescriptionImage from "../components/TitleDescriptionImage";
import { ReactComponent as FinancialDataIntegrationComponent } from "../assets/financial-data-integration.svg";
import { ReactComponent as FinancialAssetCoverageComponent } from "../assets/financial-asset-coverage.svg";
import { ReactComponent as WealthScapeSequenceDiagramComponent } from "../assets/wealth-scape-sequence-diagram.svg";
import CardWithTitleDescriptionAction from "../components/CardWithTitleDescriptionAction";
import { ReactComponent as FinancialInsightsComponent } from "../assets/financial-insights.svg";
import { useLocation } from "react-router-dom";

function navigateToSection(hash: string) {
  const element = document.getElementById(hash);
  if (element) {
    var headerOffset = 45;
    var elementPosition = element.getBoundingClientRect().top;
    var offsetPosition = elementPosition + window.scrollY - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
}

// const generalFAQs: FaqItem[] = [
//   {
//     question:
//       "How does Wealthscape get user's Financial data? What data sources does Wealthscape support?",
//     answer: (
//       <>
//         The Primary data source from which Wealthscape pulls the user's
//         Financial data is via the Account Aggregator. This includes user's bank
//         accounts, deposits, MFs, Stocks, ETFs and other asset classes. Most
//         importantly, it happens only with user's explicit consent, ensuring
//         complete control and transparency.
//         <br />
//         <br />
//         The other data sources also include fetching user's credit report via
//         Credit bureaus.
//         <br />
//         <br />
//         Wealthscape has also integrated with Market data providers to give
//         additional insights over an individual's overall portfolio.
//       </>
//     ),
//   },
//   {
//     question: "How frequently does the user's data get updated?",
//     answer: (
//       <>
//         Account data gets refreshed once everyday. The number refreshes per
//         month depends on the FIU's use case, as defined by Sahamat Use Cases We
//         Support:
//         <br />
//         a) Wealth Management (101) → 31 refreshes in a month
//         <br />
//         b) Personal Finance Management (102) → 45 refreshes in a month
//         <br />
//         <br />
//         <strong>Who Performs the Data Refresh?</strong>
//         <br />
//         a) Wealthscape runs a daily batch job to refresh customer data
//         automatically. No action required from the FIU or end-users.
//         <br />
//         b) The FIU can choose to trigger the refresh from their system, OR The
//         FIU can provide a "Refresh Data" button in their app for end-users to
//         refresh on demand.
//       </>
//     ),
//   },
//   {
//     question:
//       "Will I get XIRR calculated through Wealthscape? What if I do not have the entire historic data through MFC?",
//     answer:
//       "Yes, Wealthscape will give the XIRR. In case if the MFC historic transactions are not present, we will be giving the dates for which the calculations have been done.",
//   },
//   {
//     question: "Do we get buy price of the stocks through Wealthscape?",
//     answer:
//       "Through AA as the data source we do not get the exact buy price of the stock which the user has bought. However we do provide the day end price of the stock when the transaction was done through other data sources.",
//   },
//   {
//     question: "What is the difference between MFC and AA MF data?",
//     answer: (
//       <>
//         MFC will help pull user's historic Mutual Funds data/portfolio which
//         will include all the transactions since inception. AA can fetch data up
//         until past 20 years.
//         <br />
//         <br />
//         MFC requires OTP every single time a user gives a consent. On the other
//         hand, AA with just one OTP given, the consent will be valid for a year's
//         time.
//       </>
//     ),
//   },
//   {
//     question: "Does the FIU need to integrate directly with PRISM?",
//     answer:
//       "No. If you are using our Wealthscape platform, PRISM is already integrated. You will get categorized transactions and insights if you subscribe for it.",
//   },
//   {
//     question: "Does Wealthscape provide ISIN level insights?",
//     answer:
//       "Wealthscape has integrated market data providers like morningstar, ValueResearch, PulseLabs, etc to give an ISIN-level information and overall insights.",
//   },
//   {
//     question: "How are zero holdings handled?",
//     answer:
//       "During the consent journey of the Account Aggregator, CDSL does not discover the accounts which are having zero holdings.",
//   },
//   {
//     question: "How can I create dummy accounts to test the Wealthscape APIs?",
//     answer: (
//       <>
//         You can use our dummy banks' portal, Simbanks, to create sample accounts
//         of any FI type (Savings, RD, TD, Demat, etc.).
//         <br />
//         Link: https://simbanks.finfactor.in/
//         <br />
//         <br />
//         Once the accounts are created, proceed with the usual Wealthscape
//         consent journey and then call the analytics APIs.
//         <br />
//         <br />
//         <strong>OTPs for UAT:</strong>
//         <br />
//         1. First OTP from AA | Static: 111111 (6 times 1)
//         <br />
//         2. Finvu Bank (Dummy bank on the portal) | Dynamic OTP will be triggered
//         <br />
//         3. All other FIPs/Banks OTPs | Static: 12345678
//       </>
//     ),
//   },
// ];

const WealthScapePage: React.FC = React.memo(() => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash.length === 0) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      return;
    }

    const hash = location.hash.slice(1);
    navigateToSection(hash);
  }, [location.hash]);

  return (
    <>
      <Box
        sx={{
          paddingLeft: 4,
        }}
      >
        <TitleDescription
          title="WealthScape"
          description="Our WealthScape API seamlessly integrates diverse financial data streams, providing comprehensive, data-driven insights across bank deposits, mutual funds, and equity holdings. Powered by AA Connect, MFC Connect, and market data sources, our solution unlocks the full potential of your financial platform with rich, actionable data."
        />
        <>
          <Title title="Why WealthScape?" />
          <Grid container spacing={3} paddingTop={1}>
            <Grid size={4}>
              <TitleDescriptionImage
                title="Gain Comprehensive Financial Insights"
                description="Leverage data from bank statements, mutual fund holdings, and equity portfolios to provide users with a holistic view of their personal finances. Track transactions, balances, and key investment activities in real-time, enabling users to make informed financial decisions."
                svg={FinancialInsightsComponent}
                maxWidth={290}
              />
            </Grid>
            <Grid size={4}>
              <TitleDescriptionImage
                title="Extensive Financial Data Integration"
                description="Integrate with a wide range of financial institutions and data sources using AA Connect and MFC Connect. Seamlessly pull data from banks, brokerage firms, mutual fund providers, and market platforms such as Morningstar to deliver reliable financial insights."
                svg={FinancialDataIntegrationComponent}
                maxWidth={290}
              />
            </Grid>
            <Grid size={4}>
              <TitleDescriptionImage
                title="Full Spectrum of Financial Assets Coverage"
                description="Access and analyze a wide variety of financial asset classes, including bank accounts, mutual funds, equities, and more. Our platform connects to all major financial accounts, ensuring that users can monitor and manage their entire financial portfolio in one place."
                svg={FinancialAssetCoverageComponent}
                maxWidth={290}
              />
            </Grid>
          </Grid>
        </>
        <>
          <Title title="See how WealthScape can help you" />
          <Grid container spacing={3} paddingTop={1}>
            <Grid size={4}>
              <CardWithTitleDescriptionAction
                title="Deposit Insights"
                description="A set of APIs designed for data analytics and aggregation of bank data."
                actionText="See API Reference"
                actionClick={() => navigateToSection("deposit-insights")}
              />
            </Grid>
            <Grid size={4}>
              <CardWithTitleDescriptionAction
                title="Mutual Fund Insights"
                description="A set of APIs designed for data analytics and aggregation of mutual fund data."
                actionText="See API Reference"
                actionClick={() => navigateToSection("mutual-fund-insights")}
              />
            </Grid>
            <Grid size={4}>
              <CardWithTitleDescriptionAction
                title="Equity Insights"
                description="A set of APIs designed for data analytics and aggregation of equities data."
                actionText="See API Reference"
                actionClick={() => navigateToSection("equity-insights")}
              />
            </Grid>
          </Grid>
        </>
        <>
          <Title
            title="How WealthScape Works - Sequence Diagram"
            paddingBottom={4}
            paddingTop={6}
          />
          <WealthScapeSequenceDiagramComponent />
        </>
      </Box>
      {/* <Box
        id="general-faqs"
        sx={{
          paddingTop: 4,
          paddingLeft: 4,
          paddingRight: 4,
          paddingBottom: 2,
        }}
      >
        <FAQs faqs={generalFAQs} />
      </Box> */}
      <RapiDocReact
        spec-url={"https://dhanaprayoga.fiu.finfactor.in/pfm/v2/api-docs"}
        show-info={false}
        show-header={false}
        render-style="view"
        theme="light"
        primary-color="#6C4FE0"
        bg-color="#FFFFFF"
        text-color="#2C3E50"
        header-color="#1A202C"
        nav-bg-color="#2D3748"
        nav-text-color="#E2E8F0"
        nav-hover-bg-color="#4A5568"
        nav-hover-text-color="#FFFFFF"
        nav-accent-color="#6C4FE0"
        schema-style="table"
        regular-font='"Inter","Roboto","Helvetica","Arial",sans-serif'
        mono-font='"SF Mono","Monaco","Inconsolata","Roboto Mono",monospace'
        font-size="default"
        schema-description-expanded={true}
      >
        <span id="auth" slot="servers" /> {/* Hack to scroll to right place */}
        <span id="notifications" slot="tag--Notifications" />
        <span id="subscription" slot="tag--Subscription" />
        <span id="aa-connect" slot="tag--AA-Connect" />
        <span id="mfc-connect" slot="tag--MFC-Connect" />
        <span id="deposit-insights" slot="tag--Deposit-Insights" />
        <span id="mutual-fund-insights" slot="tag--Mutual-Fund-Insights" />
        <span id="demat-insights" slot="tag--Demat-Insights" />
        <span id="equity-insights" slot="tag--Equity-Insights" />
        <span id="etf-insights" slot="tag--ETF-Insights" />
        <span id="equity-etf-insights" slot="tag--Equity-and-ETF-Insights" />
        <span id="nps-insights" slot="tag--NPS-Insights" />
        <span id="recurring-deposit-insights" slot="tag--Recurring-Deposit-Insights"/>
        <span id="term-deposit-insights" slot="tag--Term-Deposit-Insights" />
        <span id="insurance-insights" slot="tag--Insurance-Insights" />
        <span id="credit-data" slot="tag--Credit-Data" />
        <span id="fno" slot="tag--FNO" />
        <span id="nudges-and-cohorts" slot="tag--Nudges-and-Cohorts" />
        <span id="networth" slot="tag--Net-Worth" />
        <span id="others" slot="tag--Others" />
      </RapiDocReact>
    </>
  );
});

export default WealthScapePage;
